import { useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Offcanvas from "react-bootstrap/Offcanvas";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import API from "../../api";
import "./support-center.css";

/**
 * Support Center (modernized: equal-height category boxes, list-style questions, modern drawer)
 * Data: API.get("faq") returns grouped categories [{ id,title,description,orderBy,faqs:[{id,question,answer},...] }]
 */

export default function SupportCenter() {
  const navigate = useNavigate();
  const { id: routeFaqId } = useParams();
  const location = useLocation();

  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // { id, question, answer, _catTitle, _catId }

  useEffect(() => {
    setLoading(true);
    API.get("faq")
        .then((res) => {
          const data = res?.data;
          if (Array.isArray(data) && data.length && data[0]?.faqs) setCats(sortCats(data));
          else setCats(sortCats(legacyToGrouped(data)));
        })
        .finally(() => setLoading(false));
  }, []);

  const all = useMemo(
      () => cats.flatMap((c) => (c.faqs || []).map((f) => ({ ...f, _catTitle: c.title, _catId: c.id }))),
      [cats]
  );

  const term = q.trim().toLowerCase();
  const filteredCats = useMemo(() => {
    if (!term) return cats;
    const match = (s = "") => s.toLowerCase().includes(term);
    return cats
        .map((c) => ({
          ...c,
          faqs: (c.faqs || []).filter((f) => match(f.question) || match(stripHtml(f.answer))),
        }))
        .filter((c) => c.faqs.length);
  }, [cats, term]);

  const resultCount = useMemo(
      () => filteredCats.reduce((n, c) => n + (c.faqs?.length || 0), 0),
      [filteredCats]
  );

  const openById = useCallback(
      (id, replace = true) => {
        const found = all.find((f) => String(f.id) === String(id));
        if (!found) return;
        setSelected(found);
        setOpen(true);
        if (replace) navigate(`/support/q/${found.id}`, { replace: true, state: { from: "/support" } });
      },
      [all, navigate]
  );

  useEffect(() => {
    if (!cats.length) return;
    if (routeFaqId) return void openById(routeFaqId, true);

    const hashId = (window.location.hash || "").replace("#", "");
    if (!hashId) return;
    const isQ = all.some((f) => String(f.id) === hashId);
    if (isQ) openById(hashId, true);
    else {
      const el = document.getElementById(hashId);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - getNavbarOffset();
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [cats, all, routeFaqId, openById]);

  const onQuestion = (f) => (e) => {
    e.preventDefault();
    setSelected(f);
    setOpen(true);
    navigate(`/support/q/${f.id}`, { replace: false, state: { from: location.pathname + location.search } });
  };

  const closeDrawer = () => {
    setOpen(false);
    if (routeFaqId) navigate("/support", { replace: true });
  };

  return (
      <>
        <Helmet>
          <title>Support Center | MyGolfDNA.com</title>
          <meta name="description" content="FAQ and Support Center" />
        </Helmet>

        {/* HERO + SEARCH */}
        <section className="support-hero py-5">
          <Container>
            <h1 className="text-center monument-font-black mb-2">Support Center</h1>
            <p className="text-center text-black-50 mb-4">
              Find answers fast or{" "}
              <a href="https://shop.mygolfdna.com/pages/contact" target="_blank" rel="noreferrer">contact us</a>.
            </p>

            <Form className="support-search mx-auto">
              <Form.Control
                  size="lg"
                  type="search"
                  placeholder="Search questions (e.g., “virtual lessons”, “PocketPRO”, “billing”)"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="Search FAQs"
              />
              {term && (
                  <div className="support-search-meta">
                    <Badge bg="secondary" pill>{resultCount} result{resultCount === 1 ? "" : "s"}</Badge>
                    <Button variant="link" size="sm" className="ps-2" onClick={() => setQ("")}>Clear</Button>
                  </div>
              )}
            </Form>
          </Container>
        </section>

        {/* TOPIC TILES (equal height, no icons) */}
        {!term && (
            <section className="support-topics-section">
              <Container>
                <div className="topics-header">
                  <h2 className="monument-font-black">Help Categories</h2>
                  <p className="text-black-50">Browse guides and FAQs by topic.</p>
                </div>

                <Row xs={1} sm={2} md={3} lg={3} className="g-4">
                  {cats.map((cat) => (
                      <Col key={cat.id} className="d-flex">
                        <div className="help-card flex-fill" onClick={() => smoothScrollTo(cat.id)}>
                          <div className="help-content">
                            <h4 className="help-title">{cat.title}</h4>
                            {cat.description?.trim() && (
                                <p className="help-desc">{cat.description}</p>
                            )}
                            <div className="help-cta">Read more</div>
                          </div>
                          <div className="help-count">
                            {(cat.faqs || []).length}
                          </div>
                        </div>
                      </Col>
                  ))}
                </Row>
              </Container>
            </section>
        )}

        {/* CONTENT */}
        <section className="py-4">
          <Container>
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" className="text-orange" /></div>
            ) : filteredCats.length === 0 ? (
                <Alert variant="light" className="border">No results. Try a different search term.</Alert>
            ) : (
                filteredCats.map((cat) => (
                    <div id={cat.id} key={cat.id} className="support-category">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h5 className="mb-0">{cat.title}</h5>
                        <Badge bg="light" text="dark">{(cat.faqs || []).length}</Badge>
                      </div>
                      {cat.description?.trim() && <p className="text-black-50 mb-3">{cat.description}</p>}

                      {/* Question list (full width rows) */}
                      <div className="q-list">
                        {(cat.faqs || []).map((f) => (
                            <a
                                key={f.id}
                                href={`#${f.id}`}
                                className="q-item"
                                onClick={onQuestion({ ...f, _catTitle: cat.title, _catId: cat.id })}
                            >
                              <span className="q-item-text">{f.question}</span>
                              <span className="q-item-arrow" aria-hidden="true">›</span>
                            </a>
                        ))}
                      </div>
                    </div>
                ))
            )}

            {!loading && (
                <div className="text-center pt-3">
                  <Button variant="outline-secondary" size="sm" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                    Back to top
                  </Button>
                </div>
            )}
          </Container>
        </section>

        {/* ANSWER DRAWER (modernized; full-line text, clean actions) */}
        <Offcanvas show={open} onHide={closeDrawer} placement="end" scroll backdrop className="support-offcanvas modern-offcanvas">
          <Offcanvas.Header closeButton className="oc-header">
            <div className="oc-title-wrap">
              <div className="small text-black-50">{selected?._catTitle}</div>
              <Offcanvas.Title className="oc-title">{selected?.question}</Offcanvas.Title>

              {/* INLINE LINKS (no buttons) */}
              <div className="oc-links">
                <button type="button" className="linklike" onClick={closeDrawer}>Back</button>
                <span className="dot">•</span>
                <a className="linklike" href={`/support/a/${selected?.id}`}>Full page</a>
                <span className="dot">•</span>
                <button
                    type="button"
                    className="linklike"
                    onClick={() => copyLink(selected?.id)}
                >
                  Copy link
                </button>
              </div>
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body className="oc-body oc-body--comfortable">
            {/* Answer content (no bubble wrapper, full line) */}
            {selected ? (
                <article className="answer-html answer-prose answer-fade-in">
                  <div className="answer-container" dangerouslySetInnerHTML={{ __html: selected.answer }} />
                </article>
            ) : (
                <div className="text-black-50">No question selected.</div>
            )}
          </Offcanvas.Body>
        </Offcanvas>

      </>
  );
}

/* ---------- helpers ---------- */

function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function getNavbarOffset() {
  const fixed = document.querySelector(".navbar.fixed-top");
  return fixed ? fixed.getBoundingClientRect().height + 8 : 8;
}

function smoothScrollTo(id) {
  const el = document.getElementById(String(id));
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - getNavbarOffset();
  window.scrollTo({ top: y, behavior: "smooth" });
}

function sortCats(cats) {
  if (!Array.isArray(cats)) return [];
  return [...cats].sort((a, b) => (a.orderBy || 0) - (b.orderBy || 0));
}

function legacyToGrouped(data) {
  if (!Array.isArray(data)) return [];
  if (data.length && data[0]?.faqs) return data;
  return [];
}

function copyLink(id) {
  try {
    const url = new URL(window.location.origin + `/support/q/${id}`);
    navigator.clipboard?.writeText(url.toString());
  } catch {}
}
